/**
 * src/productPages/FileConverterProductPage.js
 *
 * VISINVOKE File Converter - free utility marketing page.
 *
 * Story: you do not pick a FROM and a TO. You hand it a file, it works out what
 * the file actually is, and it offers only the outputs it can genuinely produce.
 * Composition is unlike the paid pages: a centred cinematic opening on that one
 * idea, then the breadth of the capability registry as the payload, then the
 * local-processing promise, then the honesty about what is deliberately absent.
 *
 * Sources (current local, verified):
 *   D:\Vibe Work\VISINVOKE File Converter
 *     docs/SUPPORTED_FORMATS.md - the executable capability registry (source ->
 *       available outputs -> engine -> limitations), ~90 further provenance docs
 *     src-tauri (Rust capability registry), package.json, tauri.conf.json
 *   customer-portal/src/UtilitiesPage.js - Free Utilities catalogue entry
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from '../components/ProductNav';
import { hexA } from '../RegistryComponents';
import {
  VI, MarketingPage, Wrap, Band, Eyebrow, H2, Body, SectionHead,
  BigClaim, PullStatement, ChipCloud, BackPill, ActionButton, StatusPill,
  StatRail, FlowRail, Frame, CardGrid, Callout, NoteStrip, CtaBand,
} from '../components/marketing/ViMarketing';
import { MoreInfoCinema } from '../components/moreInfo/CyberTechMoreInfoPrimitives';
import '../CyberTechDetailPage.css';

const A = '#3b82f6';
const BACK = { label: 'Free VISINVOKE Utilities', route: '/dashboard/utilities/free' };

function EditorialVisual({ src, alt, caption }) {
  return (
    <figure style={{ margin: 0 }}>
      <img
        src={src}
        alt={alt}
        width="1536"
        height="864"
        style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '16 / 9', objectFit: 'contain', borderRadius: 4, boxShadow: '0 25px 65px rgba(0,0,0,.3)' }}
      />
      <figcaption style={{ marginTop: 12, color: '#829aa3', fontSize: 12, lineHeight: 1.55 }}>
        Illustrative product visual. {caption}
      </figcaption>
    </figure>
  );
}

const JOURNEY = [
  { n: '01', title: 'Hand it a file', text: 'Drop it in or pick it. You do not tell it what the file is.' },
  { n: '02', title: 'It identifies the file', text: 'Content is inspected - not the extension. A renamed file is not taken at its word.' },
  { n: '03', title: 'It offers real outputs', text: 'Only the targets it can genuinely produce for that exact file appear. Nothing else is offered.' },
  { n: '04', title: 'You choose', text: 'Pick the output. Where a format has real options - quality, size, frame rate - you get them.' },
  { n: '05', title: 'It converts locally', text: 'Verified, version-pinned engines run on your own machine. Nothing is uploaded.' },
  { n: '06', title: 'The output is checked', text: 'Results are re-read and compared before they are handed back as a success.' },
];

// Every list below comes from the executable capability registry.
const FAMILIES = [
  {
    name: 'Images & raster',
    items: ['JPEG', 'PNG', 'WebP', 'BMP', 'GIF', 'TIFF', 'ICO', 'PNM', 'TGA', 'QOI', 'Farbfeld', 'Radiance HDR', 'OpenEXR', 'DDS', 'PSD', 'PSB', 'XCF', 'MacPaint', 'Photo CD', 'WPG', 'Krita KRA', 'GEM Raster'],
  },
  {
    name: 'Camera RAW',
    items: ['DNG', 'CR2', 'CR3', 'NEF', 'ARW', 'RAF', 'ORF', 'RW2', 'PEF'],
  },
  {
    name: 'Audio',
    items: ['WAV', 'FLAC', 'MP3', 'AAC', 'M4A', 'Ogg Vorbis', 'Opus', 'APE', 'TAK', 'TTA', 'Musepack', 'AC-3', 'E-AC-3', 'DTS', 'AMR-NB', 'AMR-WB', 'GSM', 'Speex', 'CAF', 'MOD', 'XM', 'S3M', 'IT', 'MIDI'],
  },
  {
    name: 'Video',
    items: ['MP4', 'WebM', 'Matroska', 'MOV', 'AVI', 'MPEG', 'TS', 'MTS', 'M2TS', 'ASF', 'WMV', 'FLV', '3GP', '3G2', 'OGV'],
  },
  {
    name: 'Documents & ebooks',
    items: ['TXT', 'Markdown', 'HTML', 'DOCX', 'ODT', 'PPTX', 'ODP', 'SXW', 'EPUB', 'FB2', 'PDF', 'DjVu'],
  },
  {
    name: 'Spreadsheets & data',
    items: ['CSV', 'TSV', 'XLSX', 'ODS', 'XLS', 'XLSB', 'SXC', 'DIF', 'SQLite', 'JSON', 'JSON5', 'YAML', 'TOML', 'MessagePack', 'CBOR', 'BSON', 'plist', 'NDJSON', 'XML', 'INI', 'Java properties'],
  },
  {
    name: 'Email, contacts & calendar',
    items: ['EML', 'EMLX', 'MBOX', 'Maildir', 'Outlook MSG', 'TNEF', 'vCard', 'iCalendar', 'vCalendar', 'LDIF'],
  },
  {
    name: 'Subtitles',
    items: ['SRT', 'WebVTT', 'ASS', 'SSA', 'TTML', 'DFXP', 'SAMI', 'MPL2', 'MicroDVD'],
  },
  {
    name: 'Vector, fonts & 3D',
    items: ['SVG', 'SVGZ', 'DXF', 'TTF', 'OTF', 'WOFF', 'WOFF2', 'OBJ', 'STL', 'PLY', '3MF'],
  },
  {
    name: 'Archives & streams',
    items: ['ZIP', 'TAR', 'TAR.GZ', 'TAR.BZ2', 'TAR.XZ', 'TAR.ZST', 'GZIP', 'BZIP2', 'XZ', 'Zstandard'],
  },
  {
    name: 'Textures & professional media',
    items: ['KTX1', 'KTX2', 'Basis Universal', 'ASTC', 'PVR', 'DPX sequence', 'Cineon sequence'],
  },
  {
    name: 'Geospatial, scientific & playlists',
    items: ['GeoJSON', 'KML', 'KMZ', 'TopoJSON', 'Arc/Info ASCII Grid', 'NPY', 'M3U', 'M3U8', 'PLS', 'XSPF', 'WPL', 'ASX', 'SMIL'],
  },
  {
    name: 'Legacy office & publishing',
    items: ['WPD', 'LWP', 'StarOffice SDW', 'Publisher PUB', 'QuarkXPress QXD', 'PageMaker PMD', 'Apple Pages', 'StarOffice SDA', 'StarOffice SDD', 'Apple Keynote', 'Lotus WKS', 'Quattro Pro', 'StarOffice SDC', 'Apple Numbers', 'IDML', 'Scribus SLA', 'FrameMaker MIF'],
  },
];

const CRAFT = [
  {
    sub: 'Identification',
    title: 'Content, never the extension',
    text: 'A GEM image is content-gated and never inferred from its filename. A DWG renamed to DXF is not treated as a conversion. If the bytes do not match the format, no target appears.',
  },
  {
    sub: 'Verification',
    title: 'Outputs are checked, not assumed',
    text: 'Converted files are re-read and compared before success is reported. Font conversions compare every stable table and all unscaled glyph outlines. Mesh conversions verify coordinates and winding.',
  },
  {
    sub: 'Engines',
    title: 'Pinned, verified, and named',
    text: 'LibRaw, FFmpeg, ImageMagick, LibreOffice, DjVuLibre, FluidSynth, libopus, Khronos KTX-Software, Basis Universal, astcenc - each version-pinned, hash-verified and policy-confined.',
  },
  {
    sub: 'Honesty',
    title: 'Limits travel with the capability',
    text: 'Every path states what it does not preserve - PDF raster does not keep vector semantics, Office extraction keeps ordered text and headings but not layout, RAW output is a rendered derivative, not the negative.',
  },
];

export default function FileConverterProductPage() {
  const navigate = useNavigate();
  const back = <BackPill label={BACK.label} accent={A} onClick={() => navigate(BACK.route)} />;
  const totalFormats = FAMILIES.reduce((n, f) => n + f.items.length, 0);

  return (
    <MarketingPage className="ctmi">
      <ProductNav />

      {/* ── HERO - centred, one idea ─────────────────────────────────────── */}
      <div style={{
        position: 'relative', overflow: 'hidden', minHeight: 760, display: 'flex', alignItems: 'flex-end', paddingBottom: 56,
        background: 'radial-gradient(1100px 620px at 50% -22%, ' + hexA(A, 0.26) + ' 0%, transparent 60%), '
          + 'linear-gradient(180deg, #0c1730 0%, #0c1420 58%, #0d1117 100%)',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, transparent, ' + A + ', transparent)' }} />
        <Wrap>
          <div style={{ padding: '20px 0 40px', marginBottom: 52 }}>{back}</div>

          <div style={{ maxWidth: 880 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
              <StatusPill label="Free with your VISINVOKE account" tone={VI.good} />
              <StatusPill label="Runs via DCC" tone={VI.info} />
            </div>
            <Eyebrow accent={A}>Free VISINVOKE Utility · Local conversion workspace</Eyebrow>
            <h1 style={{
              fontSize: 'clamp(4rem, 9vw, 8.4rem)', fontWeight: 900, color: VI.ink,
              letterSpacing: '-0.075em', lineHeight: 0.88, margin: '18px 0 0',
            }}>Drop in a file.<br />See what it can become.</h1>
            <p style={{ fontSize: 'clamp(17px, 1.2vw, 21px)', color: 'rgba(255,255,255,0.82)', lineHeight: 1.65, margin: '26px 0 0', maxWidth: 760 }}>
              Every other converter starts by asking you to pick a FROM and a TO, then fails once you have chosen.
              VISINVOKE File Converter examines the file you actually gave it and offers only the outputs it can
              genuinely produce - on your own machine, through verified engines.
            </p>
          </div>

          <div style={{ marginTop: 48 }}>
            <StatRail
              accent={A}
              items={[
                { v: totalFormats + '+', l: 'Formats in the registry' },
                { v: '13', l: 'Format families' },
                { v: '100%', l: 'Local', sub: 'nothing is uploaded to a web service' },
                { v: 'Free', l: 'With your VISINVOKE account' },
              ]}
            />
          </div>
        </Wrap>
      </div>

      <Band tone="deep" pad="loose">
        <Wrap size="slim">
          <figure style={{ margin: 0 }}>
            <img
              src="/product-more-info/file-converter/hero-local-conversion.png"
              alt="Different file formats moving through a local conversion process on a laptop."
              width="1672"
              height="941"
              style={{ display: 'block', width: '100%', maxWidth: '100%', height: 'auto', aspectRatio: '1672 / 941', objectFit: 'contain', borderRadius: 4, boxShadow: '0 25px 65px rgba(0,0,0,.36)' }}
            />
            <figcaption style={{ marginTop: 14, color: '#829aa3', fontSize: 12, lineHeight: 1.55 }}>
              Capability illustration: content is identified first, then only genuine local conversion targets are offered.
            </figcaption>
          </figure>
        </Wrap>
      </Band>

      <section className="ctmi-section ctmi-tint">
        <div className="ctmi-shell">
          <div className="ctmi-workspace" aria-label="Staged File Converter workspace">
            <div className="ctmi-workspace__bar"><span>File Converter</span><strong>LOCAL WORKSPACE</strong><i>No upload</i></div>
            <div className="ctmi-workspace__grid"><aside><small>Detected source</small><h3>Camera RAW</h3><dl>{[['File','DSC_1842.CR3'],['Camera','Canon EOS'],['Size','42.1 MP'],['Depth','14-bit']].map(([k,v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}</dl></aside><main className="ctmi-feed"><small>Verified targets</small><h3>Only outputs this file can genuinely become</h3><p>The source is inspected first. Compatible local engines and preservation limits determine the choices shown next.</p><div className="ctmi-roll"><span>Selected output</span><b>JP</b><b>G</b><strong>READY</strong></div><div className="ctmi-update"><b>Colour profile retained</b><b>Metadata reviewed</b><b>Destination local</b></div></main><aside><small>Output controls</small><h3>Conversion plan</h3>{[['Quality','92%'],['Dimensions','100%'],['Metadata','74%']].map(([label,fill]) => <label key={label}>{label}<i style={{'--fill':fill}} /></label>)}<p>Staged from the real registry-first conversion workflow.</p></aside></div>
            <div className="ctmi-composer"><button type="button">JPEG</button><button type="button">PNG</button><div><small>Destination</small>Local output folder · original remains untouched</div></div>
          </div>
        </div>
      </section>

      {/* ── THE DIFFERENCE ───────────────────────────────────────────────── */}
      <Band tone="wash" accent={A} pad="loose">
        <Wrap>
          <div className="vim-split" style={{ alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <Eyebrow accent={A} style={{ marginBottom: 14 }}>The thing that makes it different</Eyebrow>
              <H2 size="xl">You should not have to know what your file can turn into.</H2>
              <Body style={{ marginTop: 20, fontSize: 16.5 }}>
                Picking a conversion pair up front is how converters waste your time. You choose a target, upload,
                wait, and discover that this particular file - this codec, this colour profile, this compressed
                texture, this legacy variant - was never supported.
              </Body>
              <Body style={{ marginTop: 14, fontSize: 16.5 }}>
                Here the order is reversed. The file is identified first, from its actual content, and the interface
                then shows the outputs that exist for it. If a target is not on screen, it is not a supported path -
                and you have not lost ten minutes finding that out.
              </Body>
            </div>
            <Frame accent={A} label="Camera RAW, for example">
              <p style={{ fontSize: 15.5, color: VI.ink, fontWeight: 600, lineHeight: 1.6, margin: 0 }}>
                Hand it a CR3. The camera and dimensions are identified <em>before</em> any targets appear - and then
                JPEG, 16-bit PNG and 16-bit TIFF are offered, because those are the outputs LibRaw can genuinely
                render for that file.
              </p>
              <p style={{ fontSize: 14, color: VI.body, lineHeight: 1.7, margin: '18px 0 0' }}>
                Hand it a compressed, mipmapped KTX2 instead and no target appears at all - because that profile is
                not supported, and inventing a flattened still would be a lie about what you got back.
              </p>
            </Frame>
          </div>
        </Wrap>
      </Band>

      <MoreInfoCinema src="/product-more-info/file-converter/content-inspection.jpg" alt="Illustrative forensic apparatus revealing the internal structure of an unknown file before conversion targets are considered." eyebrow="Illustrative principle · Local by design" title="Inspect first. Convert only what is real." copy="The registry exposes a target only when a bounded reader, a lawful engine and independent render verification agree that the path is supported." items={[{label:'Source',value:'Content identified'},{label:'Target',value:'Capability verified'},{label:'Result',value:'Written locally'}]} />

      {/* ── THE JOURNEY ──────────────────────────────────────────────────── */}
      <Band tone="deep">
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="How it works"
            title="Six steps, and you make one decision"
            lead="The work of knowing what is possible belongs to the software, not to you."
            align="center"
          />
          <FlowRail steps={JOURNEY} accent={A} compact />
        </Wrap>
      </Band>

      {/* ── THE BREADTH - the payload ────────────────────────────────────── */}
      <Band pad="loose">
        <Wrap>
          <BigClaim
            kicker="What is actually in there"
            accent={A}
            align="center"
            sub="Thirteen families. Every format below is a real registered path with a named engine behind it - not a wish list. Recognising an extension is never treated as support."
          >
            This is the free one.
          </BigClaim>

          <div className="vim-g" data-c="2" style={{ marginTop: 44, gap: 20 }}>
            {FAMILIES.map((f, i) => (
              <div key={i} className="vim-pop" style={{
                minWidth: 0, background: VI.panel,
                border: '1px solid ' + hexA(A, 0.22), borderRadius: VI.r.md,
                padding: '20px 22px 22px', display: 'flex', flexDirection: 'column', gap: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 15.5, fontWeight: 900, color: VI.ink, letterSpacing: '-0.2px' }}>{f.name}</span>
                  <span style={{ fontSize: 11.5, fontWeight: 800, color: hexA(A, 0.85), whiteSpace: 'nowrap' }}>{f.items.length} formats</span>
                </div>
                <ChipCloud items={f.items} accent={A} />
              </div>
            ))}
          </div>
        </Wrap>
      </Band>

      {/* ── LOCAL ────────────────────────────────────────────────────────── */}
      <Band tone="panel">
        <Wrap>
          <Callout
            accent={A}
            eyebrow="Where your files go"
            title="Nowhere. That is the point."
            text="Web converters work by taking your file. Your tax return, your client's contract, your unreleased photographs - uploaded to a server you know nothing about, retained for a period you were not told, on terms you did not read. VISINVOKE File Converter runs the conversion on your own machine, through engines bundled with the application."
            items={[
              'No upload, no queue, no third-party service',
              'Version-pinned, hash-verified engines',
              'Policy-confined execution',
              'Runs through VISINVOKE DCC',
            ]}
          />
          <div style={{ marginTop: 42 }}>
            <EditorialVisual
              src="/product-more-info/file-converter/local-boundary.jpg"
              alt="Illustrative sealed desktop conversion chamber keeping source files and outputs inside a clear local boundary."
              caption="Files remain inside the local conversion boundary from source to user-owned destination."
            />
          </div>
        </Wrap>
      </Band>

      {/* ── CRAFT ────────────────────────────────────────────────────────── */}
      <Band>
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Why you can trust the output"
            title="A converter is only as good as the conversions it refuses to attempt"
            lead="The registry is deliberately conservative. A path only exists once a bounded reader plus independent verification has passed."
            align="split"
          />
          <CardGrid items={CRAFT} cols={4} accent={A} variant="bar" />
          <div style={{ marginTop: 42 }}>
            <EditorialVisual
              src="/product-more-info/file-converter/verified-refusal.jpg"
              alt="Illustrative independent verification gates accepting checked outputs while one unsupported path remains deliberately closed."
              caption="Verified outputs pass independent checks; an unsupported path remains closed with its evidence intact."
            />
          </div>
          <div style={{ marginTop: 30 }}>
            <PullStatement accent={A}>
              “No target is exposed until a bounded reader plus independent render verification has passed.
              Recognising an extension does not create a capability.”
            </PullStatement>
          </div>
        </Wrap>
      </Band>

      {/* ── HONESTY ──────────────────────────────────────────────────────── */}
      <Band tone="deep">
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Where it stands"
            title="Unsupported formats stay visible, and stay disabled"
            lead="They are not hidden, and they are not quietly failed at the end of a progress bar."
            align="split"
          />
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 24 }}>
            <StatusPill label="In development" tone={VI.warn} />
            <span style={{ fontSize: 15, color: VI.body, lineHeight: 1.6 }}>
              VISINVOKE File Converter is still being built. The capability registry below is real and executable,
              but the application is the authority for what a given build converts on the day you use it.
            </span>
          </div>
          <div className="vim-g" data-c="3">
            {[
              { title: 'Blocked on licensing', text: 'DWG is blocked until a lawful, redistributable engine is selected. Renaming it to DXF is never treated as conversion.' },
              { title: 'Blocked on proof', text: 'JPEG XL, HEIF/HEIC, AVIF and JPEG 2000 stay catalogue-only until codec, colour, precision and licensing closures are proven.' },
              { title: 'Blocked on honesty', text: 'PST and OST are unavailable because recognising a header is not a parser. HDF5, NetCDF and FITS are unavailable because complex multidimensional data must not be generically flattened.' },
            ].map((c, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.025)', border: '1px dashed ' + VI.lineHi,
                borderRadius: VI.r.md, padding: '20px 20px', minWidth: 0,
                display: 'flex', flexDirection: 'column', gap: 10,
              }}>
                <div style={{ fontSize: 15.5, fontWeight: 800, color: 'rgba(255,255,255,0.86)' }}>{c.title}</div>
                <p style={{ fontSize: 13.5, color: VI.muted, lineHeight: 1.65, margin: 0 }}>{c.text}</p>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 26 }}>
            <NoteStrip title="Current state">
              VISINVOKE File Converter is in development and runs through VISINVOKE DCC. The capability registry
              above is the executable one - but it is also a moving target, and the application, not this page, is
              the authority for what a given build can convert on the day you use it.
            </NoteStrip>
          </div>
        </Wrap>
      </Band>

      <CtaBand
        accent={A}
        eyebrow="Free VISINVOKE Utility"
        title="Included with your VISINVOKE account."
        text="No separate purchase, no per-conversion cost, no upload. Runs through the Desktop Command Centre alongside every other VISINVOKE utility."
        back={back}
      >
        <ActionButton accent={A} disabled pill="In development">VISINVOKE File Converter</ActionButton>
      </CtaBand>
    </MarketingPage>
  );
}
